import React, { useState, useEffect } from "react";
import { Settings, Lead } from "@/api/entities";
import { invokeFunctions } from "@/api/integrations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings as SettingsIcon, Save, Mail, RefreshCw, Plus, Trash2, FlaskConical, Sparkles } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export default function Ajustes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => Settings.list(),
  });

  const [margenGlobal, setMargenGlobal] = useState(30);
  const [costeGlobal, setCosteGlobal] = useState(5);
  const [firmaEmail, setFirmaEmail] = useState("");
  const [plantillaPresupuesto, setPlantillaPresupuesto] = useState("");
  const [configSeguimientos, setConfigSeguimientos] = useState([
    { dias: 2, prompt: "Genera un email corto y profesional preguntando si el cliente ha recibido el presupuesto y si le encaja con sus necesidades. Sé amable pero directo." },
    { dias: 4, prompt: "Genera un email breve y cordial preguntando si sigue interesado en el presupuesto. Si no está interesado, pide que nos lo indique para cerrar el expediente. Sé respetuoso." }
  ]);

  // Configuración IA — se inyecta en el prompt de sugerirRespuestaEmail.
  const [iaConfig, setIaConfig] = useState({
    tono: 'cercano',
    instrucciones: '',
    firma: '',
  });

  // #10 Auto-cierre por inactividad
  const [autoCierre, setAutoCierre] = useState({
    enabled: false,
    dias_aviso: 7,
    dias_cierre: 1,
    asunto_aviso: 'Cerramos tu solicitud si no nos confirmas nada',
    plantilla_aviso:
      'Hola {nombre},\n\n' +
      'Te escribimos para confirmar si sigues interesado en el presupuesto que te enviamos.\n\n' +
      'Si no recibimos respuesta en las próximas 24 horas, cerraremos automáticamente tu solicitud — siempre podrás reabrirla más adelante respondiendo a este email.\n\n' +
      'Quedamos atentos.\n\n' +
      'Un saludo,\nEstudi Roig',
  });

  useEffect(() => {
    const margen = settings.find(s => s.clave === 'margen_global');
    const coste = settings.find(s => s.clave === 'coste_global');
    const firma = settings.find(s => s.clave === 'firma_email');
    const plantilla = settings.find(s => s.clave === 'plantilla_presupuesto');

    if (margen) setMargenGlobal(parseFloat(margen.valor));
    if (coste) setCosteGlobal(parseFloat(coste.valor));
    if (firma) setFirmaEmail(firma.valor);
    if (plantilla) setPlantillaPresupuesto(plantilla.valor);
    
    const configSeg = settings.find(s => s.clave === 'config_seguimientos');
    if (configSeg) {
      try {
        setConfigSeguimientos(JSON.parse(configSeg.valor));
      } catch (e) {}
    }

    const autoC = settings.find(s => s.clave === 'auto_cierre_config');
    if (autoC) {
      try {
        setAutoCierre(prev => ({ ...prev, ...JSON.parse(autoC.valor) }));
      } catch (e) {}
    }

    const iaC = settings.find(s => s.clave === 'ia_config');
    if (iaC) {
      try {
        setIaConfig(prev => ({ ...prev, ...JSON.parse(iaC.valor) }));
      } catch (e) {}
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (ajustes) => {
      const promises = [];

      for (const ajuste of ajustes) {
        const existente = settings.find(s => s.clave === ajuste.clave);
        if (existente) {
          promises.push(Settings.update(existente.id, ajuste));
        } else {
          promises.push(Settings.create(ajuste));
        }
      }

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast({
        title: "Guardado",
        description: "Los ajustes se han guardado correctamente",
      });
    },
  });

  const [isProcessingEmails, setIsProcessingEmails] = useState(false);

  // #12 Simulador de auto-cierre
  const [simLeadId, setSimLeadId] = useState("");
  const [isSimulating, setIsSimulating] = useState(false);
  const { data: leadsParaSim = [] } = useQuery({
    queryKey: ['leads-for-cierre-sim'],
    queryFn: async () => {
      const all = await Lead.list({}, { orderBy: 'updated_date', orderDirection: 'desc' });
      const ESTADOS_OK = ['nuevo', 'respondido', 'presupuesto_enviado', 'contactado', 'pendiente_respuesta'];
      return all.filter(l => (l.embudo || 'recibidos') === 'recibidos' && ESTADOS_OK.includes(l.estado) && !!l.email);
    },
    staleTime: 60_000,
  });

  const handleSimularCierre = async () => {
    if (!simLeadId) {
      toast({ title: "Selecciona un lead", description: "Tienes que elegir un lead activo para simular.", variant: "destructive" });
      return;
    }
    if (!window.confirm("Esto va a ENVIAR un email REAL al cliente y CERRAR el lead. ¿Continuar?")) return;
    setIsSimulating(true);
    try {
      const res = await invokeFunctions('simularCierreLead', { lead_id: simLeadId });
      const data = res?.data || res;
      toast({
        title: "Simulación completada",
        description: `Aviso enviado: ${data.aviso_enviado ? 'sí' : 'no'} · Lead cerrado: ${data.lead_cerrado ? 'sí' : 'no'}`,
      });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-for-cierre-sim'] });
    } catch (error) {
      toast({
        title: "Error simulando cierre",
        description: error?.message || "No se pudo ejecutar la simulación",
        variant: "destructive",
      });
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSave = () => {
    const ajustes = [
      { clave: 'margen_global', valor: margenGlobal.toString(), tipo: 'global' },
      { clave: 'coste_global', valor: costeGlobal.toString(), tipo: 'global' },
      { clave: 'firma_email', valor: firmaEmail, tipo: 'plantilla' },
      { clave: 'plantilla_presupuesto', valor: plantillaPresupuesto, tipo: 'plantilla' },
      { clave: 'config_seguimientos', valor: JSON.stringify(configSeguimientos), tipo: 'plantilla' },
      { clave: 'auto_cierre_config', valor: JSON.stringify(autoCierre), tipo: 'plantilla' },
      { clave: 'ia_config', valor: JSON.stringify(iaConfig), tipo: 'plantilla' }
    ];

    saveMutation.mutate(ajustes);
  };

  const handleProcessEmails = async () => {
    setIsProcessingEmails(true);
    try {
      const response = await invokeFunctions('procesarEmailsEntrantes', {});
      const processed = response?.data?.processed || 0;
      toast({
        title: "Correos procesados",
        description: `Se han procesado ${processed} correos nuevos`,
      });
    } catch (error) {
      console.error('Error procesando emails:', error);
      toast({
        title: "Error",
        description: error?.response?.data?.error || error.message || "Error al procesar correos",
        variant: "destructive"
      });
    } finally {
      setIsProcessingEmails(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto h-full overflow-auto">
      <div className="mb-4 lg:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Ajustes Generales</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          Configura los valores por defecto y plantillas
        </p>
      </div>

      <div className="space-y-4 lg:space-y-6">
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white text-base">
              <SettingsIcon className="w-4 h-4" />
              Valores Globales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Estos valores se usan como fallback cuando una categoría o producto no tiene valores propios
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="margen" className="dark:text-gray-300 text-sm">Margen Global (%)</Label>
                <Input
                  id="margen"
                  type="number"
                  step="0.1"
                  value={margenGlobal}
                  onChange={(e) => setMargenGlobal(parseFloat(e.target.value))}
                  className="dark:bg-gray-800 dark:border-gray-700"
                />
              </div>

              <div>
                <Label htmlFor="coste" className="dark:text-gray-300 text-sm">Coste Fijo Global (€)</Label>
                <Input
                  id="coste"
                  type="number"
                  step="0.01"
                  value={costeGlobal}
                  onChange={(e) => setCosteGlobal(parseFloat(e.target.value))}
                  className="dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-gray-900 dark:text-white text-base">Plantilla de Presupuesto</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="plantilla" className="dark:text-gray-300 text-sm">Texto de introducción</Label>
              <Textarea
                id="plantilla"
                value={plantillaPresupuesto}
                onChange={(e) => setPlantillaPresupuesto(e.target.value)}
                rows={4}
                placeholder="Estimado cliente,&#10;&#10;Adjuntamos presupuesto solicitado:&#10;..."
                className="dark:bg-gray-800 dark:border-gray-700 text-sm"
              />
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-gray-900 dark:text-white text-base">Firma de Email</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="firma" className="dark:text-gray-300 text-sm">Firma que aparecerá al final de los emails</Label>
              <Textarea
                id="firma"
                value={firmaEmail}
                onChange={(e) => setFirmaEmail(e.target.value)}
                rows={4}
                placeholder="Saludos,&#10;Estudi Roig&#10;Tel: +34 123 456 789&#10;www.estudiroig.com"
                className="dark:bg-gray-800 dark:border-gray-700 text-sm"
              />
            </CardContent>
          </Card>
        </div>

        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-gray-900 dark:text-white text-base">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Seguimiento Automático de Presupuestos
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfigSeguimientos([...configSeguimientos, { dias: (configSeguimientos[configSeguimientos.length - 1]?.dias || 0) + 2, prompt: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir email
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Configura los emails de seguimiento. Se envían automáticamente si el cliente no responde.
            </p>
            
            {configSeguimientos.map((config, index) => (
              <div key={index} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email {index + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Enviar a los</span>
                      <Input
                        type="number"
                        min="1"
                        value={config.dias}
                        onChange={(e) => {
                          const nuevaConfig = [...configSeguimientos];
                          nuevaConfig[index].dias = parseInt(e.target.value) || 1;
                          setConfigSeguimientos(nuevaConfig);
                        }}
                        className="w-16 h-7 text-sm dark:bg-gray-800 dark:border-gray-700"
                      />
                      <span className="text-xs text-gray-500">días</span>
                    </div>
                  </div>
                  {configSeguimientos.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfigSeguimientos(configSeguimientos.filter((_, i) => i !== index))}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <Textarea
                  value={config.prompt}
                  onChange={(e) => {
                    const nuevaConfig = [...configSeguimientos];
                    nuevaConfig[index].prompt = e.target.value;
                    setConfigSeguimientos(nuevaConfig);
                  }}
                  rows={2}
                  placeholder="Instrucciones para la IA al generar este email..."
                  className="dark:bg-gray-800 dark:border-gray-700 text-sm"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-gray-900 dark:text-white text-base">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Auto-cierre por inactividad
              </span>
              <label className="flex items-center gap-2 text-xs font-normal cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoCierre.enabled}
                  onChange={(e) => setAutoCierre({ ...autoCierre, enabled: e.target.checked })}
                  className="h-4 w-4"
                />
                {autoCierre.enabled ? 'Habilitado' : 'Deshabilitado'}
              </label>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tras N días sin respuesta del cliente se le envía un email de aviso. Si no responde en M días más, el lead se cierra automáticamente.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-gray-300 text-sm">Días antes del aviso</Label>
                <Input
                  type="number"
                  min="1"
                  value={autoCierre.dias_aviso}
                  onChange={(e) => setAutoCierre({ ...autoCierre, dias_aviso: parseInt(e.target.value) || 1 })}
                  className="dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300 text-sm">Días tras aviso para cerrar</Label>
                <Input
                  type="number"
                  min="1"
                  value={autoCierre.dias_cierre}
                  onChange={(e) => setAutoCierre({ ...autoCierre, dias_cierre: parseInt(e.target.value) || 1 })}
                  className="dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>
            <div>
              <Label className="dark:text-gray-300 text-sm">Asunto del email de aviso</Label>
              <Input
                value={autoCierre.asunto_aviso}
                onChange={(e) => setAutoCierre({ ...autoCierre, asunto_aviso: e.target.value })}
                className="dark:bg-gray-800 dark:border-gray-700"
              />
            </div>
            <div>
              <Label className="dark:text-gray-300 text-sm">Plantilla del email de aviso</Label>
              <Textarea
                value={autoCierre.plantilla_aviso}
                onChange={(e) => setAutoCierre({ ...autoCierre, plantilla_aviso: e.target.value })}
                rows={6}
                className="dark:bg-gray-800 dark:border-gray-700 text-sm"
              />
              <p className="text-[11px] text-gray-500 mt-1">Placeholder disponible: <code>{'{nombre}'}</code></p>
            </div>

            {/* #12 Simulador */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
              <Label className="dark:text-gray-300 text-sm flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-amber-600" />
                Simular cierre sobre un lead (timers a 0)
              </Label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                Ejecuta el flujo completo (envía aviso real + cierra) sobre el lead seleccionado para verificar que la cadena funciona.
                Si el cliente responde después, #11 reabre el lead automáticamente.
              </p>
              <div className="flex gap-2">
                <select
                  value={simLeadId}
                  onChange={(e) => setSimLeadId(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">— Elige un lead activo —</option>
                  {leadsParaSim.map(l => (
                    <option key={l.id} value={l.id}>
                      [{l.estado}] {l.nombre || '(sin nombre)'} &lt;{l.email}&gt;
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleSimularCierre}
                  disabled={isSimulating || !simLeadId}
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400"
                >
                  {isSimulating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1" />}
                  Simular cierre
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white text-base">
              <Sparkles className="w-4 h-4 text-purple-600" />
              Configuración IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Estas opciones se inyectan en el prompt de Gemini cuando pulsas
              "Sugerir respuesta" sobre un email del cliente. No afectan al
              auto-cierre ni a los emails de seguimiento.
            </p>
            <div>
              <Label className="dark:text-gray-300 text-sm">Tono de respuesta</Label>
              <select
                value={iaConfig.tono}
                onChange={(e) => setIaConfig({ ...iaConfig, tono: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
              >
                <option value="formal">Formal</option>
                <option value="cercano">Cercano</option>
                <option value="comercial">Comercial</option>
              </select>
            </div>
            <div>
              <Label className="dark:text-gray-300 text-sm">Instrucciones generales</Label>
              <Textarea
                value={iaConfig.instrucciones}
                onChange={(e) => setIaConfig({ ...iaConfig, instrucciones: e.target.value })}
                rows={5}
                placeholder='Ej: "Siempre saluda con el nombre del cliente. Menciona plazo de 7 días laborables. Firma como Estudi Roig. Si piden algo fuera del catálogo (eventos grandes, lonas custom...), deriva a Abraham."'
                className="dark:bg-gray-800 dark:border-gray-700 text-sm"
              />
            </div>
            <div>
              <Label className="dark:text-gray-300 text-sm">Firma por defecto</Label>
              <Textarea
                value={iaConfig.firma}
                onChange={(e) => setIaConfig({ ...iaConfig, firma: e.target.value })}
                rows={3}
                placeholder="Un saludo,&#10;Estudi Roig"
                className="dark:bg-gray-800 dark:border-gray-700 text-sm"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Si está vacía, se usa la firma del bloque "Firma de Email" como fallback.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white text-base">
              <Mail className="w-4 h-4" />
              Procesamiento de Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              El sistema revisa automáticamente cada 5 minutos si hay correos nuevos. 
              También puedes forzar una revisión manual.
            </p>
            <Button 
              onClick={handleProcessEmails}
              disabled={isProcessingEmails}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isProcessingEmails ? 'animate-spin' : ''}`} />
              {isProcessingEmails ? "Procesando..." : "Revisar Correos Ahora"}
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-end pb-4">
          <Button 
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? "Guardando..." : "Guardar Ajustes"}
          </Button>
        </div>
      </div>
    </div>
  );
}